/**
 * API service for Exports module
 */
import { ReportGenerationRequest } from './reportsApi';

/**
 * Export service for handling report downloads
 */
export const exportsApi = {
  /**
   * Generate and download report as JSON
   */
  exportAsJSON: async (request: Omit<ReportGenerationRequest, 'export_format'>) => {
    const { reportGenerationApi } = await import('./reportsApi');
    return await reportGenerationApi.generateReport({
      ...request,
      export_format: 'json'
    });
  },

  /**
   * Generate and download report as CSV
   */
  exportAsCSV: async (request: Omit<ReportGenerationRequest, 'export_format'>) => {
    const { reportGenerationApi } = await import('./reportsApi');
    const response = await reportGenerationApi.generateReport({
      ...request,
      export_format: 'csv'
    });

    // Download file
    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  },

  /**
   * Generate and download report as Excel
   */
  exportAsExcel: async (request: Omit<ReportGenerationRequest, 'export_format'>) => {
    const { reportGenerationApi } = await import('./reportsApi');
    const response = await reportGenerationApi.generateReport({
      ...request,
      export_format: 'excel'
    });

    // Download file
    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  },

  /**
   * Generate and download report as PDF
   */
  exportAsPDF: async (request: Omit<ReportGenerationRequest, 'export_format'>) => {
    const { reportGenerationApi } = await import('./reportsApi');
    const response = await reportGenerationApi.generateReport({
      ...request,
      export_format: 'pdf'
    });

    // Download file
    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  },

  /**
   * Generic export function
   */
  exportReport: async (
    request: ReportGenerationRequest,
    format: 'json' | 'csv' | 'excel' | 'pdf'
  ) => {
    const { reportGenerationApi } = await import('./reportsApi');
    return await reportGenerationApi.generateReport(request);
  },

  /**
   * Helper to trigger file download from blob
   */
  downloadBlob: (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Helper to generate filename
   */
  generateFilename: (reportName: string, format: string): string => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const sanitizedReportName = reportName.replace(/\s+/g, '_').toLowerCase();
    return `${sanitizedReportName}_${timestamp}.${format}`;
  },
};
